import type { ComponentType, FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { useField, useFormikContext } from 'formik'
import { Grid, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import { AddressLine } from '@dhl/components'
import { useValidField } from '@dhl/inputs'
import { Check } from '@dhl/icons'
import {
  AddressFormat,
  FieldName,
  FieldFormat,
  useGetAddressByCarrierCountryPostalCodeAndHouseNumber,
  Carrier,
  useGetAddressesByCarrierAndCountry,
  useGetAddressesByCarrierCountryAndCity,
  useGetAddressesByCarrierCountryAndPostalCode,
  useAddressFormat,
  AddressesLookupResponse,
} from '@dhl/receiver-address-format-api'
import { assertNever, isNotEmpty } from '@dhl/utils'

import {
  hasStrictAutocompleteField,
  getMaxLength,
  isRequired,
  getPattern,
  getFieldFormatByName,
  hasStrictAutocompleteFields,
} from './AddressFormat.js'
import { AddressTextFieldInput } from './AddressTextFieldInput.js'
import { AddressAutocompleteFieldInput } from './AddressAutocompleteFieldInput.js'
import { Address } from './types/index.js'
import { createName } from './useAddressField.js'
import { useAddressDataLookup } from './useAddressDataLookup.js'

export const initialAddress = {
  countryCode: '',
  postalCode: '',
  houseNumber: '',
  houseNumberSuffix: '',
  street: '',
  city: '',
  suburb: '',
  additionalAddressLine: '',
}

export const createAddressFieldValidationSchema = (
  t: TFunction,
  fieldFormat?: FieldFormat,
  fieldName?: FieldName
): Yup.StringSchema<string | undefined, Yup.AnyObject, '', 'd'> => {
  if (!fieldFormat) {
    return Yup.string().default('')
  }

  const maxLength = getMaxLength(fieldFormat)
  return Yup.string()
    .default('')
    .test(
      'is-not-too-long',
      t('components.addressInput.fieldIsTooLongErrorMessage', { lengthInput: maxLength }),
      (value): boolean => {
        const max = getMaxLength(fieldFormat)
        return max ? (value || '').length <= max : true
      }
    )
    .test(
      'is-required',
      fieldName
        ? t('components.addressInput.fieldNameIsRequiredErrorMessage', {
            fieldName: t(`components.addressInput.fieldNames.${fieldName}`),
          })
        : t('components.addressInput.fieldIsRequiredErrorMessage'),
      (value): boolean => (isRequired(fieldFormat) ? (value || '').trim().length > 0 : true)
    )
    .test('is-valid', t('components.addressInput.fieldIsInvalidErrorMessage'), (value): boolean => {
      const pattern = getPattern(fieldFormat)
      return pattern && (value || '').length > 0 ? pattern.test(value || '') : true
    })
}

const getOrderedValidationFields = (t: TFunction, addressFormat: AddressFormat): Yup.Schema<Address> => {
  let fields = {}

  addressFormat.ordering?.forEach((fieldName) => {
    switch (fieldName) {
      case 'PostalCode':
        fields = {
          ...fields,
          postalCode: createAddressFieldValidationSchema(t, addressFormat.postalCodeFormat, fieldName),
        }
        break
      case 'City':
        fields = {
          ...fields,
          city: createAddressFieldValidationSchema(t, addressFormat.cityFormat, fieldName),
        }
        break
      case 'HouseNumber':
        fields = {
          ...fields,
          houseNumber: createAddressFieldValidationSchema(t, addressFormat.houseNumberFormat, fieldName),
        }
        break
      case 'HouseNumberSuffix':
        fields = {
          ...fields,
          houseNumberSuffix: createAddressFieldValidationSchema(t, addressFormat.houseNumberSuffixFormat, fieldName),
        }
        break
      case 'Street':
        fields = {
          ...fields,
          street: createAddressFieldValidationSchema(t, addressFormat.streetFormat, fieldName),
        }
        break
      case 'Suburb':
        fields = {
          ...fields,
          suburb: createAddressFieldValidationSchema(t, addressFormat.suburbFormat, fieldName),
        }
        break
      case 'AdditionalAddressLine':
        if (addressFormat.additionalAddressLineFormat) {
          fields = {
            ...fields,
            additionalAddressLine: createAddressFieldValidationSchema(
              t,
              addressFormat.additionalAddressLineFormat,
              fieldName
            ),
          }
        }
        break
      default:
      //
    }
  })

  // @ts-ignore
  return Yup.object(fields)
}

export const useCreateAddressValidationSchema = (
  carrier: Carrier,
  country: string,
  addressFormatFromProps?: AddressFormat
): Yup.Schema<Address> => {
  const { t } = useTranslation()
  const defaultAddressFormat = useAddressFormat(carrier, country)
  const addressFormat = addressFormatFromProps || defaultAddressFormat
  const { lookupByPostalCode, lookupByPostalCodeAndHouseNumber } = useAddressDataLookup()

  // Sometimes the address format is not yet available when validation starts, which causes an error (i.e. when validating first name)
  if (!addressFormat) {
    return Yup.object() as unknown as Yup.ObjectSchema<Address>
  }

  const addressValidationSchema = getOrderedValidationFields(t, addressFormat)

  // TODO: fix when rewriting component into splitted display and implementation
  // eslint-disable-next-line func-names
  return addressValidationSchema.test('is-postal-code-valid', '', async function (values): Promise<
    boolean | Yup.ValidationError
  > {
    const isLookupByPostalCode = addressFormat.lookupModes?.includes('ByPostalCode') ?? false
    const isLookupByPostalCodeAndHouseNumber =
      addressFormat.lookupModes?.includes('ByPostalCodeAndHouseNumber') ?? false

    const isPostalCodeValid = await createAddressFieldValidationSchema(
      t,
      addressFormat.postalCodeFormat,
      'PostalCode'
    ).isValid(values.postalCode)
    const isHouseNumberValid = await createAddressFieldValidationSchema(
      t,
      addressFormat.houseNumberFormat,
      'HouseNumber'
    ).isValid(values.houseNumber)

    if (!isPostalCodeValid && addressFormat.ordering?.includes('PostalCode')) {
      return this.createError({
        path: `${this.path}.postalCode`,
        message: t('components.addressInput.invalidPostalcodeOrHouseNumberErrorMessage', { ...values }),
      })
    }

    if (!isHouseNumberValid && addressFormat.ordering?.includes('HouseNumber')) {
      return this.createError({
        path: `${this.path}.houseNumber`,
        message: t('components.addressInput.invalidHouseNumberErrorMessage', { ...values }),
      })
    }

    if (
      (isLookupByPostalCode && isPostalCodeValid) ||
      (isLookupByPostalCodeAndHouseNumber && isPostalCodeValid && isHouseNumberValid)
    ) {
      // TODO: this method should fix typing of values, so no typecasting is needed
      if (hasStrictAutocompleteFields(addressFormat)) {
        try {
          let lookupResult: AddressesLookupResponse[] = []

          if (isLookupByPostalCode) {
            lookupResult = await lookupByPostalCode(carrier, addressFormat?.country, values)
          } else if (isLookupByPostalCodeAndHouseNumber) {
            lookupResult = await lookupByPostalCodeAndHouseNumber(carrier, addressFormat?.country, values)
          }

          if (!lookupResult?.length) {
            if (addressFormat.lookupModes?.includes('ByPostalCodeAndHouseNumber')) {
              if (addressFormat.ordering?.includes('PostalCode')) {
                return this.createError({
                  path: `${this.path}.postalCode`,
                  message: t('components.addressInput.invalidPostalcodeOrHouseNumberErrorMessage', { ...values }),
                })
              }
              return this.createError({
                path: `${this.path}.houseNumber`,
                message: t('components.addressInput.invalidHouseNumberErrorMessage', { ...values }),
              })
            }

            if (addressFormat.lookupModes?.includes('ByPostalCode')) {
              return this.createError({
                path: `${this.path}.postalCode`,
                message: t('components.addressInput.invalidPostalcodeErrorMessage', { ...values }),
              })
            }

            throw new Error(`Unknown lookupModes ${addressFormat.lookupModes}`)
          }
        } catch {
          return this.createError({
            path: `${this.path}.postalCode`,
            message: t('common.pleaseTryAgainErrorMessage'),
          })
        }
      }
    }

    return true
  })
}

export const useValidAddressField = (
  name: string,
  fieldName: FieldName,
  fieldFormat?: FieldFormat
): string | undefined => {
  const { t } = useTranslation()

  return useValidField(
    `${name}.${fieldName[0].toLowerCase() + fieldName.slice(1)}`,
    createAddressFieldValidationSchema(t, fieldFormat, fieldName)
  )
}

export type PreviewComponentProps = {
  address: Address
  hasExtraAddressLine?: boolean
  ['data-testid']?: string
}

export const DefaultPreviewComponent: FC<PreviewComponentProps> = ({
  address,
  hasExtraAddressLine,
  'data-testid': dataTestId,
}) => (
  <Stack flexDirection="row" alignItems="center" gap={2} data-testid={dataTestId}>
    <Stack flexGrow={1}>
      <AddressLine
        address={address}
        variant="h5"
        flex={1}
        showExtraAddressLine={
          hasExtraAddressLine && !!address.additionalAddressLine && address.additionalAddressLine?.length > 0
        }
      />
    </Stack>
    <Check htmlColor="green" />
  </Stack>
)

type Props = {
  addressFormat?: AddressFormat
  carrier?: Carrier
  country?: string
  name: string
  isAutoCompleteEnabled?: boolean
  hasExtraAddressLine?: boolean
  PreviewComponent?: ComponentType<PreviewComponentProps>
  disabled?: boolean
}

export const AddressInput: FC<Props> = ({
  addressFormat: addressFormatFromProps,
  carrier = 'parcel',
  country = 'NL',
  name,
  isAutoCompleteEnabled = false,
  hasExtraAddressLine = false,
  PreviewComponent,
  disabled,
}) => {
  const { setFieldValue, setFieldError } = useFormikContext()
  const [field] = useField<Address>(name)
  const defaultAddressFormat = useAddressFormat(carrier, country)
  const addressFormat = addressFormatFromProps || defaultAddressFormat
  const { street, city, suburb } = field.value ?? {}

  const [selectableOptions, setSelectableOptions] = useState<{
    cities: string[]
    streets: string[]
    suburbs: string[]
  } | null>(null)

  const validPostalCode = useValidAddressField(name, 'PostalCode', addressFormat?.postalCodeFormat)
  const validHouseNumber = useValidAddressField(name, 'HouseNumber', addressFormat?.houseNumberFormat)
  const validCity = useValidAddressField(name, 'City', addressFormat?.cityFormat)

  const { data: countryData, isLoading: isLoadingCountry } = useGetAddressesByCarrierAndCountry(
    carrier,
    addressFormat?.country ?? '',
    {
      query: {
        enabled: !!addressFormat?.lookupModes?.includes('ByCountry'),
        staleTime: 10 * 60 * 1000,
      },
    }
  )

  const { data: cityData, isLoading: isLoadingCity } = useGetAddressesByCarrierCountryAndCity(
    carrier,
    addressFormat?.country ?? '',
    validCity ?? '',
    {
      query: {
        enabled: !!(addressFormat?.lookupModes?.includes('ByCity') && !!validCity),
        staleTime: 10 * 60 * 1000,
      },
    }
  )

  const { data: postalCodeData, isLoading: isLoadingPostalCode } = useGetAddressesByCarrierCountryAndPostalCode(
    carrier,
    addressFormat?.country ?? '',
    validPostalCode ?? '',
    {
      query: {
        enabled: !!(addressFormat?.lookupModes?.includes('ByPostalCode') && !!validPostalCode),
        staleTime: 10 * 60 * 1000,
      },
    }
  )

  const { data: postalCodeAndHouseNumberData, isLoading: isLoadingPostalCodeAndHouseNumber } =
    useGetAddressByCarrierCountryPostalCodeAndHouseNumber(
      carrier,
      addressFormat?.country ?? '',
      validPostalCode!,
      validHouseNumber ? parseInt(validHouseNumber, 10) : 0,
      {
        query: {
          enabled: !!(
            addressFormat?.lookupModes?.includes('ByPostalCodeAndHouseNumber') &&
            validPostalCode &&
            validHouseNumber
          ),
          staleTime: 10 * 60 * 1000,
        },
      }
    )

  // Lookup by postal code
  useEffect(() => {
    if (!addressFormat?.lookupModes?.includes('ByPostalCode') || !validPostalCode) {
      return
    }

    if (!isLoadingPostalCode && !!postalCodeData?.length) {
      setFieldError(`${name}.postalCode`, '')
    }
  }, [addressFormat?.lookupModes, isLoadingPostalCode, name, postalCodeData?.length, setFieldError, validPostalCode])

  // Lookup by postalcode and house number
  useEffect(() => {
    if (!addressFormat?.lookupModes?.includes('ByPostalCodeAndHouseNumber') || !validPostalCode || !validHouseNumber) {
      return
    }

    if (!isLoadingPostalCodeAndHouseNumber && !!postalCodeAndHouseNumberData?.length) {
      setFieldError(`${name}.postalCode`, '')
      setFieldError(`${name}.houseNumber`, '')
    }
  }, [
    addressFormat?.lookupModes,
    isLoadingPostalCodeAndHouseNumber,
    name,
    postalCodeAndHouseNumberData?.length,
    setFieldError,
    validHouseNumber,
    validPostalCode,
  ])

  const addressData = useMemo(() => {
    if (addressFormat?.lookupModes?.includes('ByCity') && cityData) {
      return cityData
    }

    if (addressFormat?.lookupModes?.includes('ByPostalCode') && postalCodeData) {
      return postalCodeData
    }

    if (addressFormat?.lookupModes?.includes('ByPostalCodeAndHouseNumber') && postalCodeAndHouseNumberData) {
      return postalCodeAndHouseNumberData
    }

    if (addressFormat?.lookupModes?.includes('ByCountry') && countryData) {
      return countryData
    }

    return []
  }, [addressFormat?.lookupModes, cityData, countryData, postalCodeAndHouseNumberData, postalCodeData])

  const isLoading = isLoadingCountry || isLoadingCity || isLoadingPostalCode || isLoadingPostalCodeAndHouseNumber

  // Process lookup result for all lookup modes and store selectable options
  useEffect(() => {
    if (addressData) {
      const cities = [
        ...new Set(
          addressData
            .map(({ city: mappedCity }) => mappedCity)
            .filter(isNotEmpty)
            .sort()
        ),
      ]

      const streets = [
        ...new Set(
          addressData
            .filter((entry) => entry.city === city)
            .flatMap((res) => res.streets)
            .filter(isNotEmpty)
        ),
      ].sort()

      const suburbs = [
        ...new Set(
          addressData
            .filter((entry) => entry.city === city)
            .flatMap((res) => res.suburbs)
            .filter(isNotEmpty)
        ),
      ].sort()

      setSelectableOptions({
        cities,
        streets,
        suburbs,
      })
    }
  }, [city, addressData, setSelectableOptions])

  // Process new selectable options
  useEffect(() => {
    if (selectableOptions) {
      const { cities, streets, suburbs } = selectableOptions

      if ((hasStrictAutocompleteField(addressFormat?.cityFormat) && !cities.includes(city)) || city === '') {
        setFieldValue(`${name}.city`, cities.length >= 1 ? cities[0] : '')
      }

      if ((hasStrictAutocompleteField(addressFormat?.streetFormat) && !streets.includes(street)) || street === '') {
        setFieldValue(`${name}.street`, streets.length >= 1 ? streets[0] : '')
      }

      if ((hasStrictAutocompleteField(addressFormat?.suburbFormat) && !suburbs.includes(suburb)) || suburb === '') {
        setFieldValue(`${name}.suburb`, suburbs.length >= 1 ? suburbs[0] : '')
      }
    }
  }, [name, addressFormat, selectableOptions, setFieldValue, city, street, suburb])

  const PreviewComponentDisplay = PreviewComponent ?? DefaultPreviewComponent

  return (
    <Grid spacing={2} container>
      {addressFormat?.ordering?.map((fieldName) => {
        const fieldFormat = getFieldFormatByName(addressFormat, fieldName)

        switch (fieldFormat?.fieldKind) {
          case 'NotApplicable':
            return null

          case 'Autocomplete': {
            const options = (() => {
              switch (fieldName) {
                case 'City':
                  return selectableOptions ? selectableOptions.cities : []
                case 'Street':
                  return selectableOptions ? selectableOptions.streets : []
                case 'Suburb':
                  return selectableOptions ? selectableOptions.suburbs : []
                default:
                  throw new Error(`Field kind 'Autocomplete' not yet support for field name ${fieldName}`)
              }
            })()

            if (isLoading || options.length > 0 || !fieldFormat.isStrict) {
              return (
                <Grid key={fieldName} size={{ xs: fieldFormat.fieldSize.mobile, sm: fieldFormat.fieldSize.desktop }}>
                  <AddressAutocompleteFieldInput
                    name={createName(name, fieldName)}
                    fieldName={fieldName}
                    fieldFormat={fieldFormat}
                    options={options}
                    isLoading={isLoading}
                    disabled={disabled}
                  />
                </Grid>
              )
            }

            return null
          }

          case 'Text': {
            return (
              <Grid key={fieldName} size={{ xs: fieldFormat.fieldSize.mobile, sm: fieldFormat.fieldSize.desktop }}>
                <AddressTextFieldInput
                  name={createName(name, fieldName)}
                  fieldName={fieldName}
                  fieldFormat={fieldFormat}
                  isAutoCompleteEnabled={isAutoCompleteEnabled}
                  fullWidth
                  disabled={disabled}
                />
              </Grid>
            )
          }

          default:
            return fieldFormat ? assertNever(fieldFormat) : null
        }
      })}

      {addressFormat?.isPreviewVisible && addressData && addressData.length > 0 && (
        <Grid size={12}>
          <PreviewComponentDisplay address={field.value} hasExtraAddressLine={hasExtraAddressLine} />
        </Grid>
      )}
    </Grid>
  )
}
